import { Component, EventEmitter, Input, Output, SimpleChanges } from "@angular/core";
import { IGenericPreference } from "@app/interfaces/IGenericPreference";
import { IPreferencesContent } from "@app/interfaces/IPreferencesContent";
import { Currency } from "@enums/Currency.enum";
import { DurationFormat } from "@enums/DurationFormat.enum";
import { DistanceMeasurement } from "@enums/measurements/DistanceMeasurement.enum";
import { FuelConsumptionMeasurement } from "@enums/measurements/FuelConsumptionMeasurement.enum";
import { TemperatureMeasurement } from "@enums/measurements/TemperatureMeasurement.enum";
import { VolumeMeasurement } from "@enums/measurements/VolumeMeasurement";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { PreferencesService } from "@services/preferences.service";
import "./report-internationalisation-options.component.scss";

@Component({
    selector: "report-internationalisation-options",
    template: require("./report-internationalisation-options.component.html")
})
export class ReportInternationalisationOptionsComponent {

    @Input() report: IReportDefinition;
    @Output() reportChange = new EventEmitter<IReportDefinition>();

    preferences: ICombinedPreferences;

    // ---

    Currency = Currency;
    DurationFormat = DurationFormat;
    VolumeMeasurement = VolumeMeasurement;
    DistanceMeasurement = DistanceMeasurement;
    TemperatureMeasurement = TemperatureMeasurement;
    FuelConsumptionMeasurement = FuelConsumptionMeasurement;

    loading: boolean;

    constructor(private readonly preferencesService: PreferencesService) { }

    onChanged(): void {
        // ensures other components are ready before emitting
        setTimeout(() => {
            this.cacheCustomPreferences(this.report);
            this.reportChange.emit(this.report);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.prepareReport(this.report);
    }

    updateReport(report: IReportDefinition): void {
        this.prepareReport(this.report);
    }

    // ---

    async prepareReport(report: IReportDefinition): Promise<void> {
        if (!report) {
            return;
        }

        if (!this.hasMissingPreferences(report)) {
            this.onChanged();
            return;
        }

        this.preferences = await this.preferencesService.getAllPreferences();
        this.addMissingReportPreferences(report, this.preferences);
        this.onChanged();
    }

    private addMissingReportPreferences(report: IReportDefinition, preferences: ICombinedPreferences): void {
        if (!Currency[report.Currency]) {
            report.Currency = preferences.Currency.Value;
        }

        if (!VolumeMeasurement[report.VolumeMeasurement]) {
            report.VolumeMeasurement = preferences.VolumeMeasurement.Value;
        }

        if (!DistanceMeasurement[report.DistanceMeasurement]) {
            report.DistanceMeasurement = preferences.DistanceMeasurement.Value;
        }

        if (!TemperatureMeasurement[report.TemperatureMeasurement]) {
            report.TemperatureMeasurement = preferences.TemperatureMeasurement.Value;
        }

        if (!FuelConsumptionMeasurement[report.FuelConsumptionMeasurement]) {
            report.FuelConsumptionMeasurement = preferences.FuelConsumptionMeasurement.Value;
        }

        if (!DurationFormat[report.DurationFormat]) {
            report.DurationFormat = DurationFormat.DaysHoursMinutesSeconds;
        }
    }

    private hasMissingPreferences(report: IReportDefinition): boolean {
        return !Currency[report.Currency]
            || !VolumeMeasurement[report.VolumeMeasurement]
            || !DistanceMeasurement[report.DistanceMeasurement]
            || !TemperatureMeasurement[report.TemperatureMeasurement]
            || !FuelConsumptionMeasurement[report.FuelConsumptionMeasurement];
    }

    private cacheCustomPreferences(report: IReportDefinition): void {
        if (!report) {
            return;
        }

        const newCachedCustomPreferences: IPreferencesContent = {}

        newCachedCustomPreferences.Currency = this.createPreferenceForCaching<Currency>(report.Currency);
        newCachedCustomPreferences.VolumeMeasurement = this.createPreferenceForCaching<VolumeMeasurement>(report.VolumeMeasurement);
        newCachedCustomPreferences.DistanceMeasurement = this.createPreferenceForCaching<DistanceMeasurement>(report.DistanceMeasurement);
        newCachedCustomPreferences.TemperatureMeasurement = this.createPreferenceForCaching<TemperatureMeasurement>(report.TemperatureMeasurement);
        newCachedCustomPreferences.FuelConsumptionMeasurement = this.createPreferenceForCaching<FuelConsumptionMeasurement>(report.FuelConsumptionMeasurement);

        report.cachedCustomPreferences = newCachedCustomPreferences;
    }

    private createPreferenceForCaching<T>(value: any): IGenericPreference<T> {
        const newGenericPreference: IGenericPreference<T> = {
            Value: value,
            Section: -1
        }

        return newGenericPreference;
    }
}
